import { unwrap } from "@sagot/api-client";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import type { AddonCatalogOut, AddonKey } from "./api-types";
import { wsKeys } from "./query-keys";

/** `GET /v1/billing/addons`: add-ons priced in the workspace's current currency (DEC-042). */
export function useAddonCatalog(enabled = true): UseQueryResult<AddonCatalogOut[]> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: wsKeys.addonCatalog(wsId), queryFn: () => unwrap(api.GET("/v1/billing/addons")), staleTime: 10 * 60_000, enabled });
}

/** What one unit costs in the subscription's interval; a year of a recurring add-on is 10 months (DEC-135). */
export interface AddonPrice {
  /** The price book amount: per month for a recurring add-on, once for a pack. */
  price: string;
  currency: string;
  /** One unit for one `per` (the monthly price, or ten months of it for a year). */
  intervalPrice: string;
  /** One unit if bought now: `intervalPrice`, prorated to the days left in a paid year. */
  dueNow: string;
  /** The subscription's interval; `null` for a one-time add-on (a message pack). */
  per: "month" | "year" | null;
  /** Days left / in the paid year, only when `dueNow` is prorated. */
  proration: { daysRemaining: number; cycleDays: number } | null;
}

/** The catalogue row as an {@link AddonPrice}. */
export function addonPriceOf(row: AddonCatalogOut): AddonPrice {
  const proration = row.days_remaining !== null && row.cycle_days !== null ? { daysRemaining: row.days_remaining, cycleDays: row.cycle_days } : null;
  return {
    price: row.price,
    currency: row.currency,
    intervalPrice: row.interval_price,
    dueNow: row.due_now,
    per: row.interval === null ? null : row.interval === "annual" ? "year" : "month",
    proration,
  };
}

/** One add-on's price + currency, `undefined` while loading or when not sold in the currency. */
export function useAddonPrice(key: AddonKey, enabled = true): AddonPrice | undefined {
  const catalog = useAddonCatalog(enabled);
  const found = catalog.data?.find((addon) => addon.key === key);
  return found ? addonPriceOf(found) : undefined;
}

/** i18next options that pick the "… / year" copy (`key_year`) on an annual subscription. */
export function yearContext(per: AddonPrice["per"]): { context?: string } {
  return per === "year" ? { context: "year" } : {};
}
